import Typography from '@material-ui/core/Typography'
import Page from 'organization/Event/Page'
import React, {useState} from 'react'
import Box from '@material-ui/core/Box'
import {Grid, withStyles} from '@material-ui/core'
import {
  useClickFunnels,
  useServices,
} from 'organization/Event/Services/ServicesProvider'
import ImportAttendeesButton from 'organization/Event/Services/Apps/ClickFunnels/Config/ImportAttendeesButton'
import {useToggle} from 'lib/toggle'
import ErrorAlert from 'lib/ui/alerts/ErrorAlert'
import InfoAlert from 'lib/ui/alerts/InfoAlert'
import TagSelector from 'organization/Event/Services/Apps/ClickFunnels/Config/Tags/TagSelector'
import {
  ATTENDEE_CHECKED_IN,
  ATTENDEE_CREATED,
  ATTENDEE_SIGNED_WAIVER,
  IMPORT_TAG,
  Tag,
} from 'organization/Event/Services/Apps/ClickFunnels/Config/Tags'
import Button from 'lib/ui/Button'
import {spacing} from 'lib/ui/theme'
import {useOrganization} from 'organization/OrganizationProvider'
import {useEvent} from 'Event/EventProvider'
import {
  ClickFunnelsIntegration,
  LoginField,
} from 'organization/Event/Services/Apps/ClickFunnels'
import {api} from 'lib/url'
import GroupsConfig from 'organization/Event/Services/Apps/ClickFunnels/Config/Groups/GroupsConfig'
import {Group} from 'organization/Event/Services/Apps/ClickFunnels/Config/Groups/group'
import AutocompleteFields, {
  clickFunnelsEmptyField,
  ClickFunnelsField,
} from 'organization/Event/Services/Apps/ClickFunnels/Config/Fields/AutocompleteFields'

export interface UpdateData {
  tags: Tag[]
  groups: Group[]
  login_field: LoginField
}

export default function Config() {
  const integration = useClickFunnels()
  const {save, processing} = useSave()

  const [tags, setTags] = useState<Tag[]>(integration.tags)
  const [groups, setGroups] = useState<Group[]>(integration.groups)
  const {flag: showingImportSuccess, toggle: toggleImportSuccess} = useToggle()
  const [importError, setImportError] = useState('')
  const clearImportError = () => setImportError('')

  const [loginField, setLoginField] = useState<LoginField>({
    id: integration.login_field_id ?? '',
    key: integration.login_field_key ?? '',
    label: integration.login_field_label ?? '',
    type: integration.login_field_type ?? '',
  })

  const tagForType = (type: Tag['type']) => {
    const target = tags.find((t) => t.type === type)
    if (!target) {
      throw new Error(`Unknown tag type: ${type}`)
    }

    return target
  }

  const updateTag = (tag: Tag) => {
    const updated = tags.map((t) => {
      const isTarget = t.id === tag.id

      if (isTarget) {
        return tag
      }

      return t
    })

    setTags(updated)
  }

  const changeLoginField = (field: ClickFunnelsField | null) => {
    setLoginField(field ?? clickFunnelsEmptyField)
  }

  const handleSave = () => {
    const data: UpdateData = {
      tags,
      groups,
      login_field: loginField,
    }

    save(data)
  }

  return (
    <Page>
      <Box mb={3}>
        <Grid container spacing={2}>
          <Grid item md={6}>
            <Typography variant="h4">Click Funnels</Typography>
          </Grid>
          <StyledGrid item md={6}>
            <ImportAttendeesButton
              onSuccess={toggleImportSuccess}
              onError={setImportError}
            />
          </StyledGrid>
        </Grid>
      </Box>

      <ErrorAlert onClose={clearImportError}>{importError}</ErrorAlert>
      <InfoAlert showing={showingImportSuccess}>
        Import successfully started. Depending on the number of subscribers.
      </InfoAlert>

      <Box mb={3}>
        <Box mb={2}>
          <Typography variant="h5">
            Attendee Import or Delete via Tag
          </Typography>
        </Box>
        <TagSelector tag={tagForType(IMPORT_TAG)} onChange={updateTag} />
        <em>
          If an attendee with the given email already exists, obv.io will update
          the other attributes.
        </em>
      </Box>

      <Box mb={2}>
        <Typography variant="h5">Login URL Field</Typography>
        <em>
          Select the Custom Field in Click funnels to save your attendee's Login
          URL.
        </em>
      </Box>

      <AutocompleteFields
        onChange={changeLoginField}
        inputVariant={'outlined'}
        value={loginField}
        type={'custom'}
      />

      <Box mb={3}>
        <Typography variant="h5">Action Tags</Typography>
        <em>
          The following tags can be applied in Click Funnels when an attendee
          takes the specific action. (optional)
        </em>
      </Box>

      <TagSelector tag={tagForType(ATTENDEE_CREATED)} onChange={updateTag} />
      <TagSelector tag={tagForType(ATTENDEE_CHECKED_IN)} onChange={updateTag} />
      <TagSelector
        tag={tagForType(ATTENDEE_SIGNED_WAIVER)}
        onChange={updateTag}
      />

      <Box mb={3}>
        <Typography variant="h5">Groups</Typography>
        <em>
          Select additional custom fields you wish to import into your Obvio
          event. (optional)
        </em>
      </Box>
      <Box mb={5}>
        <GroupsConfig onChange={setGroups} groups={groups} />
      </Box>

      <SaveButton
        type="submit"
        variant="contained"
        color="primary"
        size="large"
        aria-label="save"
        onClick={handleSave}
        disabled={processing}
        fullWidth
      >
        Save
      </SaveButton>
    </Page>
  )
}

function useSave() {
  const {client} = useOrganization()
  const {event} = useEvent()

  const [processing, setProcessing] = useState<boolean>(false)
  const {update: updateIntegration} = useServices()
  const [error, setError] = useState<string | null>(null)

  const save = (data: UpdateData) => {
    setProcessing(true)
    setError('')

    client
      .put<ClickFunnelsIntegration>(
        api(`/events/${event.id}/integrations/click_funnels`),
        data,
      )
      .then(updateIntegration)
      .catch((e) => setError(e.message))
      .finally(() => {
        setProcessing(false)
      })
  }

  return {
    processing,
    error,
    save,
  }
}

const StyledGrid = withStyles({
  root: {
    display: 'flex',
    justifyContent: 'end',
  },
})(Grid)

const SaveButton = withStyles({
  root: {
    marginBottom: spacing[5],
  },
})(Button)
